<script setup lang="ts">
import { computed, ref } from 'vue';

interface Props {
    modelValue: boolean; // controls visibility
    termsHtml?: string; // optional pre-rendered terms HTML (overrides default)
}
const props = defineProps<Props>();
const emit = defineEmits<{
    (e: 'update:modelValue', v: boolean): void;
    (e: 'accepted'): void;
    (e: 'declined'): void;
}>();

const visible = computed({
    get: () => props.modelValue,
    set: (v: boolean) => emit('update:modelValue', v),
});
const agreed = ref(false);

function onAccept() {
    if (!agreed.value) return;
    emit('accepted');
}
function onDecline() {
    emit('declined');
}

/** If you pass `termsHtml`, it will override this default. */
const DEFAULT_TERMS_HTML = `
  <article class="terms">
    <h1>Duelly Terms &amp; Conditions</h1>
    <p><strong>Effective Date:</strong> January 1, 2025</p>
    <p>Welcome to Duelly, Inc. (“Duelly,” “we,” “our,” “us”). These Terms of Use (“Terms”) govern your use of the Duelly mobile application, web platform, and related services (collectively, the “Services”). By creating an account or using our Services, you agree to these Terms. If you do not agree, do not use Duelly.</p>

    <h2>1. Eligibility</h2>
    <ul>
      <li>You must be at least 13 years old to use Duelly. If you are under 18, you may only use Duelly with the consent and supervision of a parent or guardian.</li>
      <li>You must comply with all applicable laws when using Duelly.</li>
      <li>We may restrict or terminate access if we reasonably believe you are not eligible or are misusing the Services.</li>
    </ul>

    <h2>2. Your Account</h2>
    <ul>
      <li>You must provide accurate, current, and complete information when registering.</li>
      <li>You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.</li>
      <li>You may not impersonate another person, misrepresent your identity, or create an account for someone else without permission.</li>
      <li>We reserve the right to suspend or terminate accounts at our sole discretion.</li>
    </ul>

    <h2>3. User Content &amp; License</h2>
    <ul>
      <li>You retain ownership of the videos, photos, posts, comments, and other content you create (“User Content”).</li>
      <li>By submitting User Content, you grant Duelly a non-exclusive, royalty-free, transferable, sub-licensable, worldwide license to host, use, distribute, modify, display, reproduce, and create derivative works for the purposes of operating, promoting, and improving Duelly.</li>
      <li>This license continues even if you delete your content, subject to reasonable retention (e.g., backups, legal obligations, investigations).</li>
      <li>You represent that you own or have rights to all User Content you post and that it does not infringe or violate the rights of others.</li>
    </ul>

    <h2>4. Community Standards</h2>
    <p>You agree not to post or share:</p>
    <ul>
      <li>Nudity or sexually explicit content.</li>
      <li>Hate speech, harassment, threats, or discriminatory content.</li>
      <li>Violence, self-harm, or dangerous activity.</li>
      <li>False information, spam, scams, or fraudulent schemes.</li>
      <li>Content that violates law or intellectual property rights.</li>
    </ul>
    <p>Duelly may remove content or restrict accounts that violate these standards.</p>

    <h2>5. Challenges, Rewards &amp; Promotions</h2>
    <ul>
      <li>Certain challenges may offer rewards or prizes. Eligibility and compliance will be verified before awarding.</li>
      <li>Taxes, fees, or legal obligations related to rewards are the responsibility of the user.</li>
      <li>By participating in challenges, you grant Duelly the right to use your User Content for promotional purposes, including showcasing submissions in marketing, advertising, and social channels.</li>
    </ul>

    <h2>6. Intellectual Property</h2>
    <ul>
      <li>Duelly owns the Services, including the platform, trademarks, branding, and technology.</li>
      <li>Except as expressly permitted, you may not copy, modify, distribute, reverse-engineer, or exploit any part of the Services.</li>
    </ul>

    <h2>7. Advertising &amp; Sponsored Content</h2>
    <ul>
      <li>Duelly may display ads, sponsored content, or promotions alongside User Content.</li>
      <li>By using Duelly, you agree that Duelly may use your User Content in connection with ads and promotions without additional compensation.</li>
      <li>If you post branded or sponsored content, you must comply with applicable laws and disclose partnerships.</li>
    </ul>

    <h2>8. Third-Party Services</h2>
    <p>Duelly may contain links or integrations to third-party services. We are not responsible for third-party content, terms, or policies.</p>

    <h2>9. Data &amp; Privacy</h2>
    <p>Use of Duelly is also governed by our Privacy Policy, which explains how we collect, use, and share information.</p>

    <h2>10. Disclaimers</h2>
    <p>Duelly is provided on an “as is” and “as available” basis without warranties of any kind. We do not guarantee uninterrupted service, error-free operation, or accuracy of content.</p>

    <h2>11. Limitation of Liability</h2>
    <p>To the fullest extent permitted by law, Duelly will not be liable for indirect, incidental, or consequential damages. Our total liability for any claims arising from use of the Services shall not exceed the greater of (a) $100 or (b) the amount you paid us in the past 12 months.</p>

    <h2>12. Indemnification</h2>
    <p>You agree to indemnify and hold harmless Duelly, its affiliates, employees, and partners from claims, damages, losses, or expenses arising from: (i) your use of the Services, (ii) your violation of these Terms, or (iii) your infringement of others’ rights.</p>

    <h2>13. Termination</h2>
    <ul>
      <li>You may delete your account at any time.</li>
      <li>We may suspend or permanently terminate your account without notice if you violate these Terms or use the Services in harmful ways.</li>
    </ul>

    <h2>14. Changes to Terms</h2>
    <ul>
      <li>We may update these Terms from time to time.</li>
      <li>We will notify you of material changes (e.g., via app notice or email).</li>
      <li>Continued use of the Services after changes means you accept the updated Terms.</li>
    </ul>

    <h2>15. Governing Law &amp; Disputes</h2>
    <p>These Terms are governed by the laws of the State of Florida, United States, without regard to conflict-of-law principles. Disputes may be resolved in the state or federal courts located in Florida, unless arbitration is required by law. You waive the right to participate in class actions against Duelly.</p>

    <h2>16. Severability</h2>
    <p>If any provision of these Terms is found unenforceable, the remaining provisions remain in full force.</p>

    <h2>17. Contact Us</h2>
    <address>
      Duelly, Inc.<br />
      1506 SE 16th Street<br />
      Cape Coral, FL 33990<br />
      Email: <a href="mailto:contact@duelly.net">contact@duelly.net</a>
    </address>
  </article>
`;
</script>

<template>
    <transition name="fade">
        <div v-if="visible" class="tos-backdrop">
            <div class="tos-sheet" role="dialog" aria-modal="true" aria-label="Duelly Terms of Use">
                <header class="tos-header">
                    <h2>Terms of Use</h2>
                    <p class="sub">Effective Date: January 1, 2025</p>
                </header>

                <section class="tos-body">
                    <!-- Scrollable legal content -->
                    <div class="tos-scroll">
                        <div class="tos-html" v-html="termsHtml || DEFAULT_TERMS_HTML"></div>
                    </div>

                    <label class="agree-row">
                        <input type="checkbox" v-model="agreed" />
                        <span>I have read and agree to the Duelly Terms of Use.</span>
                    </label>
                </section>

                <footer class="tos-footer">
                    <button class="btn ghost" @click="onDecline">Decline</button>
                    <button class="btn primary" :disabled="!agreed" @click="onAccept">
                        Accept
                    </button>
                </footer>
            </div>
        </div>
    </transition>
</template>

<style scoped lang="scss">
.tos-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(2px);
    display: grid;
    place-items: center;
    z-index: 4000;
}

/* FLEX column so the middle can shrink and scroll */
.tos-sheet {
    width: min(900px, 92vw);
    max-height: min(86vh, 940px);
    background: #111;
    color: #eee;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
}

.tos-header {
    padding: 20px 22px 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    flex: 0 0 auto;
}
.tos-header h2 {
    margin: 0;
    font-size: 22px;
}
.sub {
    opacity: 0.7;
    margin: 4px 0 0;
    font-size: 12px;
}

/* KEY: allow the body to shrink and give height to the scroll area */
.tos-body {
    padding: 12px 22px 8px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    flex: 1 1 auto;
    min-height: 0; /* <- essential for scroll child */
}

.tos-scroll {
    flex: 1 1 auto;
    min-height: 0; /* <- essential for scroll child */
    overflow: auto;
    -webkit-overflow-scrolling: touch; /* smoother iOS */
    padding-right: 6px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
    touch-action: pan-y; /* hints to browsers that vertical scroll is intended */
}

.tos-html {
    padding: 16px;
    line-height: 1.55;
    word-break: break-word;
}
.tos-html h1 {
    font-size: 20px;
    margin: 0 0 6px;
}
.tos-html h2 {
    font-size: 16px;
    margin: 18px 0 8px;
}
.tos-html p {
    margin: 10px 0;
}
.tos-html ul {
    margin: 8px 0 8px 20px;
}
.tos-html li {
    margin: 6px 0;
}
.tos-html address {
    font-style: normal;
    line-height: 1.6;
}

.agree-row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    user-select: none;
    flex: 0 0 auto;
}

.tos-footer {
    padding: 14px 22px 18px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    flex: 0 0 auto;
}

.btn {
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid transparent;
    cursor: pointer;
    font-weight: 600;
}
.btn.ghost {
    background: transparent;
    color: #ddd;
    border-color: rgba(255, 255, 255, 0.18);
}
.btn.primary {
    background: #00c1de;
    color: #001318;
}
.btn.primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
